import {HttpContextToken, HttpEvent, HttpHandlerFn, HttpRequest} from '@angular/common/http';
import {inject} from '@angular/core';
import {catchError, Observable, throwError, timeout} from 'rxjs';
import {MessageDialogService} from '../dialog/message-dialog/message-dialog.service';
import {IGNORE_5XX_ERROR} from './http5xx-interceptor';

export const IGNORE_EXTERNAL_API_ERROR = new HttpContextToken<boolean>(() => false);
const EXTERNAL_API_PATTERNS: RegExp[] = [
  /\/api\/gdx(\/|$)/i,
  /\/public\/api\/thaid(\/|$)/i
];

const EXTERNAL_NOT_FOUND_STATUS: ReadonlySet<number> = new Set([404]);
const EXTERNAL_TIMEOUT_MS = 20000;

let blockingDialogOpen = false;

export function externalApiErrorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const messageDialog = inject(MessageDialogService);

  const optedOut = req.context.get(IGNORE_EXTERNAL_API_ERROR);
  const isExternal = !optedOut && EXTERNAL_API_PATTERNS.some((pattern) => pattern.test(req.url));

  if (!isExternal) {
    return next(req);
  }

  const taggedRequest = req.clone({context: req.context.set(IGNORE_5XX_ERROR, true)});

  return next(taggedRequest).pipe(
    timeout(EXTERNAL_TIMEOUT_MS),
    catchError((error: unknown) => {
      if (!isExternalApiError(error) || blockingDialogOpen) {
        return throwError(() => error);
      }

      blockingDialogOpen = true;
      const status = (error as { status?: unknown }).status;
      const backendMessage = extractBackendMessage(error);
      const content = status === 422 && backendMessage ? backendMessage : buildContent(error, req.url);
      messageDialog
        .open({
          type: 'error',
          title: status === 422 ? 'ตรวจสอบข้อมูลไม่ผ่าน' : 'ระบบเชื่อมต่อภายนอกขัดข้อง',
          content,
          confirmButtonMsg: 'รับทราบ'
        })
        .subscribe({
          complete: () => {
            blockingDialogOpen = false;
          },
          error: () => {
            blockingDialogOpen = false;
          }
        });

      return throwError(() => error);
    })
  );
}

function isExternalApiError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number') {
    if (EXTERNAL_NOT_FOUND_STATUS.has(status)) {
      return false;
    }
    return true;
  }
  if ((error as { name?: string }).name === 'TimeoutError') {
    return true;
  }
  return false;
}

function buildContent(error: unknown, requestUrl: string): string {
  const serviceName = describeService(requestUrl);
  const isErrorObj = error && typeof error === 'object';
  const status = isErrorObj ? (error as { status?: number }).status : undefined;
  const isTimeout = isErrorObj && (error as { name?: string }).name === 'TimeoutError';
  const backendMessage = extractBackendMessage(error);
  const suffix = backendMessage ? ` (${backendMessage})` : '';

  if (isTimeout || status === 0) {
    return [
      `ไม่สามารถเชื่อมต่อ${serviceName}ได้ในขณะนี้`,
      'ไม่มีการตอบกลับหรือหมดเวลา',
      'กรุณาลองใหม่อีกครั้ง'
    ].join('\n');
  }

  const label = statusLabel(status ?? -1);
  const statusText = `HTTP ${status}${label ? ' ' + label : ''}`;

  let description: string;
  let recommendation: string;
  if (status === 502 || status === 503 || status === 504) {
    description = `${serviceName} ไม่พร้อมให้บริการชั่วคราว`;
    recommendation = 'กรุณาลองใหม่อีกครั้งในภายหลัง';
  } else if (status === 403) {
    description = `${serviceName} ปฏิเสธการเชื่อมต่อ`;
    recommendation = 'กรุณาติดต่อผู้ดูแลระบบ';
  } else {
    description = `${serviceName} เกิดข้อผิดพลาด`;
    recommendation = 'กรุณาลองใหม่อีกครั้ง หรือติดต่อผู้ดูแลระบบ';
  }

  return [description, `${statusText}${suffix}`, recommendation].join('\n');
}

function extractBackendMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '';
  }
  const body = (error as { error?: unknown }).error;
  if (!body) {
    return '';
  }
  if (typeof body === 'string') {
    const trimmed = body.trim();
    return trimmed === 'Internal Server Error' ? '' : trimmed;
  }
  if (typeof body === 'object') {
    const obj = body as { message?: unknown; error?: unknown };
    const candidate = obj.message ?? obj.error;
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      return trimmed === 'Internal Server Error' ? '' : trimmed;
    }
  }
  return '';
}

function describeService(url: string): string {
  if (/\/api\/gdx(\/|$)/i.test(url)) {
    return 'บริการตรวจสอบข้อมูลนิติบุคคล (GDX)';
  }
  if (/\/public\/api\/thaid(\/|$)/i.test(url)) {
    return 'บริการยืนยันตัวตนแบบดิจิทัล (ThaiD)';
  }
  return 'บริการระบบภายนอก';
}

function statusLabel(status: number): string {
  switch (status) {
    case 400:
      return 'Bad Request';
    case 401:
      return 'Unauthorized';
    case 403:
      return 'Forbidden';
    case 404:
      return 'Not Found';
    case 408:
      return 'Request Timeout';
    case 500:
      return 'Internal Server Error';
    case 502:
      return 'Bad Gateway';
    case 503:
      return 'Service Unavailable';
    case 504:
      return 'Gateway Timeout';
    default:
      return '';
  }
}
